import { RootState } from '@/store/store'
import { createSelector } from '@reduxjs/toolkit'
import { FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { selectFileProcessingStatusesWithKeys } from '@/store/selectors/file-upload-tasks-reducers.selector'

/**
 * Selector: Conversation Selected Files
 *
 * Returns selected files from the conversation form state.
 * Normalizes all selected files to an array of strings.
 */
export const selectConversationSelectedFiles = createSelector(
  (state: RootState, props: { conversationId: string }) => state.agentConversationFormState.forms[props.conversationId ?? ''],
  (agentConversationForm): string[] => {
    if (!agentConversationForm) return []

    const user_file_ids = agentConversationForm.requestParams.user_files_rag?.user_file_ids ?? []
    const file_paths = agentConversationForm.requestParams.file_paths ?? []
    const template_paths = agentConversationForm.requestParams.template_paths ?? []
    const writing_sample_file_ids = agentConversationForm.requestParams.writing_sample_files?.user_file_ids ?? []
    // Dedup: an id that appears in more than one list (e.g. a restored conversation with the same
    // user_file as both a RAG source and the writing sample) must not be double-counted — it feeds
    // the visible file count and the embeddings/token-limit math.
    return Array.from(new Set([...user_file_ids, ...file_paths, ...template_paths, ...writing_sample_file_ids]))
  }
)

/**
 * Selector: Processing Statuses For Selected Files
 */
export const selectProcessingStatusesForSelectedFiles = createSelector(
  (state: RootState, props: { chatId: string | undefined }) => selectConversationSelectedFiles(state, { conversationId: props.chatId ?? '' }),
  (state: RootState, props: { keys: string[] }) => selectFileProcessingStatusesWithKeys(state, props),
  (selectedFiles, processingStatuses) => {
    return selectedFiles.reduce((acc: Record<string, string>, key: string) => {
      acc[key] = processingStatuses[key] ?? FileProcessingStatus.SUCCESS
      return acc
    }, {})
  }
)

/**
 * Select: Conversation Embeddings Count
 *
 * Computes the total embeddings count for a conversation by summing the cached
 * embeddings counts for all selected files that are ready (successfully processed).
 *
 * This is derived/computed state - the actual source of truth is fileEmbeddingsCache.
 */
export const selectConversationEmbeddingsCount = createSelector(
  // Input 1: Selected files for this conversation
  (state: RootState, props: { chatId: string }) => selectConversationSelectedFiles(state, { conversationId: props.chatId }),
  // Input 2: File embeddings cache (source of truth)
  (state: RootState) => state.selectedFilesState.fileEmbeddingsCache,
  // Input 3: Processing statuses to filter ready files
  (state: RootState) => state.uploadFileTasksState.processingStatuses,
  // Output: Computed total embeddings count
  (selectedFiles, fileEmbeddingsCache, processingStatuses) => {
    // Only count files that are ready (not PENDING or PROCESSING)
    return selectedFiles
      .filter((filePath) => {
        const status = processingStatuses[filePath]
        // Include if: no status (existing file) OR status is SUCCESS (processing complete)
        return !status || status === FileProcessingStatus.SUCCESS
      })
      .reduce((sum, filePath) => sum + (fileEmbeddingsCache[filePath] ?? 0), 0)
  }
)

/**
 * Select: Conversation Is Over Token Limit
 * Returns true if the conversation's total embeddings exceed the token limit.
 */
export const selectFilesIsOverTokenLimit = createSelector(
  // Input selector: token limit
  (state: RootState) => state.selectedFilesState.tokenLimit.response,
  // Input selector: selected files token count
  selectConversationEmbeddingsCount,
  (tokenLimitResponse, embeddingsCount) => {
    // Calculate the maximum allowable embeddings count based on the token limit and chunk size
    const maxTokens = tokenLimitResponse.max_tokens
    const chunkSize = tokenLimitResponse.chunk_size
    const maxEmbeddings = maxTokens / chunkSize

    // Return true / false if the embeddings count exceeds the limit
    const exceedsLimit = embeddingsCount > maxEmbeddings
    return exceedsLimit
  }
)
